'use client'

import React from 'react'

import { twMerge } from 'tailwind-merge'

import type { TopCategory } from '@/types/usaspending'

import { Currency, Percentage } from './Formatters'

export interface DistributionBarProps {
  items: TopCategory[]
  title?: string
  showAmount?: boolean
  className?: string
  maxItems?: number
}

const BAR_COLORS = [
  'bg-blue-500 dark:bg-blue-600',
  'bg-indigo-500 dark:bg-indigo-600',
  'bg-purple-500 dark:bg-purple-600',
  'bg-teal-500 dark:bg-teal-600',
  'bg-amber-500 dark:bg-amber-600',
]

/**
 * Distribution Bar Component
 *
 * Displays a horizontal bar chart showing distribution of values.
 * Common uses: top recipients, spending by category, etc.
 */
export const DistributionBar: React.FC<DistributionBarProps> = ({
  items,
  title,
  showAmount = true,
  className,
  maxItems = 5,
}) => {
  const displayItems = items.slice(0, maxItems)

  return (
    <div className={className}>
      {title && (
        <h3 className="mb-4 text-sm font-bold uppercase tracking-wider text-primary-900 dark:text-primary-100">
          {title}
        </h3>
      )}

      <div className="space-y-3">
        {displayItems.map((item, index) => (
          <div key={item.code || item.name || index} className="group">
            <div className="mb-1 flex items-center justify-between">
              <span className="text-sm font-medium text-primary-800 dark:text-primary-200">
                {item.name}
                {item.code && (
                  <span className="ml-2 font-mono text-xs text-primary-400 dark:text-primary-500">
                    {item.code}
                  </span>
                )}
              </span>
              <span className="text-sm text-primary-600 dark:text-primary-400">
                {showAmount ? (
                  <Currency value={item.amount} compact />
                ) : (
                  <Percentage value={item.percentage} />
                )}
              </span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-primary-100 dark:bg-primary-800">
              <div
                className={twMerge(
                  'h-full rounded-full transition-all duration-500',
                  BAR_COLORS[index % BAR_COLORS.length],
                )}
                style={{ width: `${item.percentage}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
